import React, { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { HERO_FLIPS } from "../mock";
import { Link } from "react-router-dom";

const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_neon-workshop-3/artifacts/r7hwkp82_ChatGPT_Image_Jul_10__2026__11_39_43_AM-removebg-preview.png";

const Hero = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_FLIPS.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative hero-gradient min-h-[100vh] flex items-center overflow-hidden pt-6">
      {/* Ambient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 diagonal-lines" />
        {/* Dramatic rose glow behind the product */}
        <div
          className="absolute right-[-8%] top-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-[0.22] blur-3xl"
          style={{ background: "radial-gradient(circle, #D89999 0%, transparent 60%)" }}
        />
        <div className="absolute top-10 left-6 md:left-10 w-32 h-24 dotted-pattern opacity-60" />
        <div className="absolute bottom-10 left-6 md:left-10 w-32 h-24 dotted-pattern opacity-60" />
      </div>

      {/* Corner marks */}
      <div className="absolute top-6 left-6 w-3 h-3 border-t border-l border-rose" />
      <div className="absolute top-6 right-6 w-3 h-3 border-t border-r border-rose" />
      <div className="absolute bottom-6 left-6 w-3 h-3 border-b border-l border-rose" />
      <div className="absolute bottom-6 right-6 w-3 h-3 border-b border-r border-rose" />

      {/* Giant floating product on the right - the true hero */}
      <div className="absolute right-[-4%] md:right-[-2%] top-1/2 -translate-y-1/2 w-[70vw] md:w-[58vw] lg:w-[52vw] max-w-[900px] pointer-events-none z-0">
        <img
          src={PRODUCT_IMG}
          alt="Whip it Up Body Butter"
          className="w-full h-auto object-contain"
          style={{ filter: "drop-shadow(0 40px 80px rgba(216,153,153,0.35)) drop-shadow(0 20px 40px rgba(0,0,0,0.6))" }}
        />
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 relative z-10 grid md:grid-cols-12 gap-6 items-center w-full py-16">
        <div className="md:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-rose" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-rose">// CATALYST — 001 · BATCH 087 LIVE</span>
          </div>

          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_neon-workshop-3/artifacts/8muchqej_whip_it_up.png"
            alt="Whip it Up"
            className="h-14 md:h-20 w-auto object-contain -mb-2"
          />

          <h1 className="font-display text-[18vw] md:text-[11vw] leading-[0.82] tracking-tighter uppercase text-off-white">
            <span className="block">Whip</span>
            <span className="block">It <span className="text-rose">Up.</span></span>
          </h1>

          <div className="mt-8 h-[42px] md:h-[54px] overflow-hidden relative">
            <div key={idx} className="flip-in absolute inset-0 flex items-center">
              <span className="text-lg md:text-2xl text-off-white/85 font-light">
                Whip up <span className="text-rose">{HERO_FLIPS[idx]}</span>
              </span>
            </div>
          </div>

          <p className="mt-6 max-w-xl text-off-white/60 font-light leading-relaxed">
            Micro-batch body butter, body oil, and spray oil — crafted with love,
            naturally. Whipped for absorption. Zero fillers. Just the softest skin
            you've ever had.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-6 relative z-20">
            <Link
              to="/product/body-butter"
              className="btn-blueprint group inline-flex items-center gap-3 h-14 px-8 bg-rose text-charcoal font-mono text-[12px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only"
            >
              SHOP BODY BUTTER — $35
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 duration-300" />
            </Link>
            <Link to="/shop" className="kinetic-link font-mono text-[12px] tracking-[0.25em] text-off-white/80 hover:text-off-white">
              VIEW ALL PRODUCTS →
            </Link>
          </div>

          {/* Compact spec strip */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 max-w-2xl border-t border-off-white/10 pt-5">
            {[
              ["Base", "Triple butter"],
              ["Whip", "Double, cold"],
              ["Fillers", "Zero"],
              ["Ship", "48 hours"]
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <span className="font-mono text-[9px] tracking-[0.3em] text-off-white/40 uppercase">{k}</span>
                <span className="text-sm text-off-white mt-0.5">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column: floating label for the product */}
        <div className="md:col-span-5 hidden md:flex flex-col items-end justify-end self-end pb-6 relative z-10 pointer-events-none">
          <div className="text-right">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// FEATURED PRODUCT</p>
            <p className="font-display text-3xl uppercase text-off-white mt-2">Body Butter</p>
            <p className="font-mono text-[11px] tracking-[0.2em] text-off-white/60 mt-1">8 OZ · $35 USD</p>
            <div className="flex items-center gap-2 justify-end mt-3">
              <span className="w-2 h-2 bg-rose animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/60">IN STOCK</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
